import { useCallback, useEffect, useRef, useState } from 'react';
import { Share2, X } from 'lucide-react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import QRCode from 'qrcode';

type ShareModalProps = {
  uuid: string;
};

export default function ShareModal({ uuid }: ShareModalProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const appLink = `mezzviewerapp://view?uuid=${uuid}`;

  const canvasRef = useCallback(
    (el: HTMLCanvasElement | null) => {
      if (el) {
        QRCode.toCanvas(el, appLink, { width: 160, margin: 0 });
      }
    },
    [appLink],
  );

  useEffect(() => {
    return () => {
      if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
    };
  }, []);

  const handleCopyUrl = useCallback(async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
    copiedTimeout.current = setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setCopied(false);
  }, []);

  return (
    <>
      <button
        className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white/90 hover:bg-white/10 active:bg-white/15 transition-all cursor-pointer"
        onClick={() => setOpen(true)}
      >
        <Share2 className="size-4" />
        <span className="hidden sm:inline">Share</span>
      </button>

      <Dialog open={open} onClose={handleClose} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-brand-dark m-0">
                Share this view
              </DialogTitle>
              <button
                onClick={handleClose}
                className="rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                aria-label="Close"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Link
                </label>
                <div className="flex">
                  <input
                    readOnly
                    value={window.location.href}
                    onClick={handleCopyUrl}
                    className="flex-1 min-w-0 rounded-l-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-shadow"
                  />
                  <button
                    onClick={handleCopyUrl}
                    className={`rounded-r-lg border border-l-0 px-4 py-2.5 text-sm font-medium transition-all cursor-pointer ${
                      copied
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="flex flex-col items-center pt-2">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 self-start">
                  QR Code
                </label>
                <canvas ref={canvasRef} className="rounded-lg" />
                <p className="text-xs text-gray-400 mt-2">
                  Scan to open in the MezzViewer mobile app
                </p>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
